export type SubagentStatus =
  | 'queued'
  | 'running'
  | 'recovering'
  | 'waiting_for_approval'
  | 'waiting_for_subagents'
  | 'completed'
  | 'cancelled'
  | 'failed';

/** Public root-stream projection only; child context and tool payloads stay server-side. */
export interface SubagentStatusRow {
  id: string;
  label?: string;
  task: string;
  status: SubagentStatus;
  model: string;
  revision: number;
  summary?: string;
  error?: string;
  pendingApproval?: {
    id: string;
    title: string;
    description: string;
    contentSha256: string;
    revision: number;
  };
}

const statuses = new Set<SubagentStatus>([
  'queued', 'running', 'recovering', 'waiting_for_approval', 'waiting_for_subagents',
  'completed', 'cancelled', 'failed',
]);

export function parseSubagentStatus(value: unknown): SubagentStatusRow | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  if (
    typeof row.id !== 'string' || !row.id ||
    typeof row.task !== 'string' ||
    typeof row.model !== 'string' ||
    typeof row.status !== 'string' || !statuses.has(row.status as SubagentStatus) ||
    typeof row.revision !== 'number' || !Number.isSafeInteger(row.revision) || row.revision < 0
  ) return null;
  const approval = row.pendingApproval as Record<string, unknown> | null | undefined;
  const pendingApproval = approval &&
    typeof approval.id === 'string' && typeof approval.title === 'string' &&
    typeof approval.description === 'string' && typeof approval.contentSha256 === 'string' &&
    typeof approval.revision === 'number' && Number.isSafeInteger(approval.revision)
    ? { id: approval.id, title: approval.title, description: approval.description,
        contentSha256: approval.contentSha256, revision: approval.revision }
    : undefined;
  return {
    id: row.id,
    task: row.task,
    model: row.model,
    status: row.status as SubagentStatus,
    revision: row.revision,
    ...(typeof row.label === 'string' ? { label: row.label } : {}),
    ...(typeof row.summary === 'string' ? { summary: row.summary } : {}),
    ...(typeof row.error === 'string' ? { error: row.error } : {}),
    ...(pendingApproval ? { pendingApproval } : {}),
  };
}

export function upsertSubagentStatus(
  current: readonly SubagentStatusRow[],
  value: unknown
): SubagentStatusRow[] {
  const row = parseSubagentStatus(value);
  if (!row) return current as SubagentStatusRow[];
  const index = current.findIndex(item => item.id === row.id);
  if (index < 0) return [...current, row];
  if (current[index].revision >= row.revision) return current as SubagentStatusRow[];
  const next = [...current];
  next[index] = row;
  return next;
}

/** A reset is authoritative for membership, while newer local rows survive stale snapshots. */
export function reconcileSubagentStatuses(
  current: readonly SubagentStatusRow[],
  values: unknown
): SubagentStatusRow[] {
  if (!Array.isArray(values)) return [];
  let next: SubagentStatusRow[] = [];
  for (const value of values) next = upsertSubagentStatus(next, value);
  return next.map(row => {
    const prior = current.find(item => item.id === row.id);
    return prior && prior.revision > row.revision ? prior : row;
  });
}

export function canCancelSubagent(row: SubagentStatusRow): boolean {
  return !['completed', 'cancelled', 'failed'].includes(row.status);
}

/** Root fences stop all unfinished children without inventing a child event revision. */
export function cancelActiveSubagentStatuses(
  current: readonly SubagentStatusRow[]
): SubagentStatusRow[] {
  return current.map(row => {
    if (!canCancelSubagent(row)) return row;
    const next = { ...row, status: 'cancelled' as const };
    delete next.pendingApproval;
    return next;
  });
}

export function subagentCancelRequest(sessionId: string, turnId: string, agentId: string) {
  return { action: 'subagents.cancel', sessionId, turnId, agentId };
}

export function subagentApprovalRequest(
  sessionId: string,
  turnId: string,
  row: SubagentStatusRow,
  decision: 'approved' | 'rejected'
) {
  if (!row.pendingApproval || row.status !== 'waiting_for_approval') return null;
  return {
    action: 'subagents.approval', sessionId, turnId, agentId: row.id,
    approvalId: row.pendingApproval.id, contentSha256: row.pendingApproval.contentSha256,
    revision: row.pendingApproval.revision, decision,
  };
}

export function isChildExecutionEvent(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  const event = value as Record<string, unknown>;
  return event.type !== 'subagent_status' && event.type !== 'preview_capture_request' && (
    typeof event.agentId === 'string' ||
    typeof event.subAgent === 'object' && event.subAgent !== null ||
    event.executionMode === 'sub_agent'
  );
}
